/**
* @license Apache-2.0
*
* Copyright (c) 2026 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

import { factory as factory } from '@stdlib/ndarray-matrix-ctor';


// MAIN //

/**
* Returns a single-precision floating-point matrix (i.e., a two-dimensional ndarray).
*
* @name Float32Matrix
* @type {Function}
* @param {(NonNegativeInteger|NonNegativeIntegerArray|ArrayLikeObject<Collection>|ArrayBuffer|Iterable)} [arg0] - shape, number of rows, array-like object, ArrayBuffer, or iterable
* @param {NonNegativeInteger} [arg1] - number of columns or byte offset
* @param {(NonNegativeInteger|NonNegativeIntegerArray)} [arg2] - shape or number of rows
* @param {NonNegativeInteger} [arg3] - number of columns
* @param {Options} [options] - function options
* @param {boolean} [options.readonly=false] - boolean indicating whether to return a read-only matrix
* @param {string} [options.mode='throw'] - specifies how to handle indices which exceed matrix dimensions
* @param {StringArray} [options.submode=[options.mode]] - specifies how to handle subscripts which exceed array dimensions on a per dimension basis
* @param {string} [options.order='row-major'] - memory layout (either row-major or column-major)
* @throws {TypeError} first argument must be either a shape, integer, array-like object, ArrayBuffer, iterable, or options object
* @throws {TypeError} must provide valid options
* @returns {ndarray} two-dimensional ndarray
*
* @example
* import getShape from '@stdlib/ndarray-shape';
*
* var v = new Float32Matrix();
* // returns <ndarray>
*
* var len = getShape( v );
* // returns [ 0, 0 ]
*
* @example
* import getShape from '@stdlib/ndarray-shape';
*
* var v = new Float32Matrix( [ 2, 2 ] );
* // returns <ndarray>
*
* var len = getShape( v );
* // returns [ 2, 2 ]
*
* @example
* import getShape from '@stdlib/ndarray-shape';
*
* var v = new Float32Matrix( 2, 2 );
* // returns <ndarray>
*
* var len = getShape( v );
* // returns [ 2, 2 ]
*
* @example
* import getShape from '@stdlib/ndarray-shape';
*
* var v = new Float32Matrix( [ [ 1.0, 2.0 ], [ 3.0, 4.0 ] ] );
* // returns <ndarray>
*
* var len = getShape( v );
* // returns [ 2, 2 ]
*
* @example
* import ArrayBuffer from '@stdlib/array-buffer';
* import getShape from '@stdlib/ndarray-shape';
*
* var buf = new ArrayBuffer( 32 );
* var v = new Float32Matrix( buf );
* // returns <ndarray>
*
* var len = getShape( v );
* // returns [ 1, 8 ]
*
* @example
* import ArrayBuffer from '@stdlib/array-buffer';
* import getShape from '@stdlib/ndarray-shape';
*
* var buf = new ArrayBuffer( 32 );
* var v = new Float32Matrix( buf, 16 );
* // returns <ndarray>
*
* var len = getShape( v );
* // returns [ 1, 4 ]
*
* @example
* import ArrayBuffer from '@stdlib/array-buffer';
* import getShape from '@stdlib/ndarray-shape';
*
* var buf = new ArrayBuffer( 64 );
* var v = new Float32Matrix( buf, 16, [ 2, 1 ] );
* // returns <ndarray>
*
* var len = getShape( v );
* // returns [ 2, 1 ]
*
* @example
* import ArrayBuffer from '@stdlib/array-buffer';
* import getShape from '@stdlib/ndarray-shape';
*
* var buf = new ArrayBuffer( 64 );
* var v = new Float32Matrix( buf, 16, 2, 1 );
* // returns <ndarray>
*
* var len = getShape( v );
* // returns [ 2, 1 ]
*/
var Float32Matrix = factory( 'float32' );


// EXPORTS //

export default Float32Matrix;
